import { DialogWindow } from '@brainfocus/web/components/dialog_window/dialog_window';
import { getCurrentUser } from '@brainfocus/web/services/user';
import { getVersionInfo, ChangelogEntry } from '@brainfocus/web/services/version';
import './whats_new_modal.scss';

interface WhatsNewModalProps {
    onClose: () => void;
}

/**
 * Modal displaying changelog/What's New information.
 *
 * Shows unreleased changes (if any) and recent version entries.
 */
export function WhatsNewModal({ onClose }: WhatsNewModalProps) {
    const versionInfo = getVersionInfo();
    const currentUser = getCurrentUser();
    const isSuperAdmin = currentUser?.role === 'super_admin';

    return (
        <DialogWindow onClose={onClose}>
            <div class='bf-whats-new-modal'>
                <header class='bf-whats-new-modal__header'>
                    <h2>What's New</h2>
                    <button
                        class='bf-whats-new-modal__close'
                        onClick={onClose}
                        type='button'
                        aria-label='Close'
                    >
                        &times;
                    </button>
                </header>

                <div class='bf-whats-new-modal__content'>
                    {isSuperAdmin && versionInfo.hasUnreleasedChanges && versionInfo.unreleasedEntry && (
                        <ChangelogSection
                            entry={versionInfo.unreleasedEntry}
                            isHighlighted={true}
                        />
                    )}

                    {versionInfo.recentEntries.map(entry => (
                        <ChangelogSection
                            key={entry.version}
                            entry={entry}
                        />
                    ))}

                    {!versionInfo.hasUnreleasedChanges && versionInfo.recentEntries.length === 0 && (
                        <p class='bf-whats-new-modal__empty'>No changelog entries available.</p>
                    )}
                </div>

                {isSuperAdmin && (
                    <footer class='bf-whats-new-modal__footer'>
                        <a
                            href='https://github.com/BrainFocus-devel/brainfocus/blob/master/CHANGELOG.md'
                            target='_blank'
                            rel='noopener noreferrer'
                            class='bf-whats-new-modal__full-link'
                        >
                            View full changelog on GitHub
                        </a>
                    </footer>
                )}
            </div>
        </DialogWindow>
    );
}

interface ChangelogSectionProps {
    entry: ChangelogEntry;
    isHighlighted?: boolean;
}

/**
 * Renders a single changelog version section.
 */
function ChangelogSection({ entry, isHighlighted = false }: ChangelogSectionProps) {
    const sectionOrder = ['Added', 'Changed', 'Fixed', 'Removed', 'Security', 'Deprecated'];

    // Sort sections by preferred order
    const sortedSections = Object.entries(entry.sections).sort(([a], [b]) => {
        const indexA = sectionOrder.indexOf(a);
        const indexB = sectionOrder.indexOf(b);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return (
        <section class={`bf-changelog-section ${isHighlighted ? 'bf-changelog-section--highlighted' : ''}`}>
            <h3 class='bf-changelog-section__title'>
                {entry.isUnreleased
                    ? (
                        <>
                            <span>Unreleased</span>
                            <span class='bf-changelog-section__dev-badge'>Development</span>
                        </>
                        )
                    : (
                        <>
                            <span>v{entry.version}</span>
                            {entry.date && <span class='bf-changelog-section__date'>{entry.date}</span>}
                        </>
                        )}
            </h3>

            {sortedSections.map(([sectionName, items]) => (
                <div key={sectionName} class='bf-changelog-section__group'>
                    <h4 class={`bf-changelog-section__group-title bf-changelog-section__group-title--${sectionName.toLowerCase()}`}>
                        {sectionName}
                    </h4>
                    <ul class='bf-changelog-section__list'>
                        {items.map((item, index) => (
                            <li key={index} class='bf-changelog-section__item'>
                                {formatChangelogItem(item)}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </section>
    );
}

/**
 * Format a changelog item, removing PR references for cleaner display.
 */
function formatChangelogItem(item: string): string {
    // Remove PR references like (#123) for cleaner UI display
    return item.replace(/\s*\(#\d+\)\s*$/, '');
}
